/**
 * 模組程式關聯 API 服務
 */

import apiClient from '../axios.js';
import { normalizeProgramPermissions, normalizePermissions } from '@/utils/permissions.js';

/**
 * 模組程式關聯服務
 */
export const moduleProgramService = {
  /**
   * 查詢模組的程式列表
   * @param {string} moduleId - 模組ID
   * @returns {Promise} API 回應
   */
  async getProgramsByModuleId(moduleId) {
    const response = await apiClient.get(`/system/modules/${moduleId}/programs`);
    return response;
  },

  /**
   * 查詢模組可選的程式列表（排除已關聯的程式）
   * @param {string} moduleId - 模組ID
   * @returns {Promise} API 回應
   */
  async getAvailablePrograms(moduleId) {
    const response = await apiClient.get(`/system/modules/${moduleId}/programs/available`);
    return response;
  },

  /**
   * 批量添加程式到模組
   * @param {string} moduleId - 模組ID
   * @param {Array<string>} programIds - 程式ID列表
   * @param {Object} programPermissions - 程式權限設定 { programId: [permission1, permission2] 或 null }
   * @returns {Promise} API 回應
   */
  async addPrograms(moduleId, programIds, programPermissions = null) {
    const payload = { programIds };
    if (programPermissions) {
      // 將三段式權限 (chatmate:knowledge:read) 轉為二段式 (chatmate_knowledge:read)
      payload.programPermissions = normalizeProgramPermissions(programPermissions);
    }
    const response = await apiClient.post(`/system/modules/${moduleId}/programs`, payload);
    return response;
  },

  /**
   * 批量設定模組的程式（會先清空現有關聯）
   * @param {string} moduleId - 模組ID
   * @param {Array<string>} programIds - 程式ID列表
   * @returns {Promise} API 回應
   */
  async setPrograms(moduleId, programIds) {
    const response = await apiClient.put(`/system/modules/${moduleId}/programs`, { programIds });
    return response;
  },

  /**
   * 從模組移除程式
   * @param {string} moduleId - 模組ID
   * @param {string} programId - 程式ID
   * @returns {Promise} API 回應
   */
  async removeProgram(moduleId, programId) {
    const response = await apiClient.delete(`/system/modules/${moduleId}/programs/${programId}`);
    return response;
  },

  /**
   * 更新模組中程式的允許權限
   * @param {string} moduleId - 模組ID
   * @param {string} programId - 程式ID
   * @param {Array|null} allowedPermissions - 允許的權限陣列，null表示允許全部
   * @returns {Promise} API 回應
   */
  async updateProgramPermissions(moduleId, programId, allowedPermissions) {
    const normalized = allowedPermissions === null ? null : normalizePermissions(allowedPermissions);
    const response = await apiClient.patch(
      `/system/modules/${moduleId}/programs/${programId}/permissions`,
      { allowedPermissions: normalized }
    );
    return response;
  },

  /**
   * 查詢程式的模組列表
   * @param {string} programId - 程式ID
   * @returns {Promise} API 回應
   */
  async getModulesByProgramId(programId) {
    const response = await apiClient.get(`/system/modules/programs/${programId}/modules`);
    return response;
  }
};
